import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import './index.css';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { initSentry } from './lib/sentryClient';
import { publicPaths } from './lib/publicSeo';

initSentry();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const pathname = window.location.pathname.replace(/\/$/, '') || '/';
const isStaticPublicRoute = pathname !== '/' && publicPaths.includes(pathname);
// The server always renders the default Polish/light presentation. A saved
// language or theme cookie changes the first client render, so replace the
// static shell in that case instead of hydrating incompatible markup.
const hasPersonalizedSettings = /(?:^|;\s*)schedi_(?:language|dark_mode)=/.test(document.cookie);

async function mount() {
  // The home route retains HomeDispatcher semantics so an existing session still
  // opens the application. Other public routes hydrate their static HTML without
  // constructing AuthProvider or fetching any private data.
  const AppModule = isStaticPublicRoute ? null : await import('./App');
  const PublicAppModule = isStaticPublicRoute ? await import('./PublicApp') : null;
  const App = AppModule?.default;
  const PublicApp = PublicAppModule?.default;
  const tree = (
    <React.StrictMode>
      {isStaticPublicRoute && PublicApp ? <PublicApp /> : App ? <App /> : null}
      <Analytics />
      <SpeedInsights />
    </React.StrictMode>
  );
  if (isStaticPublicRoute && rootElement.hasChildNodes() && !hasPersonalizedSettings) {
    hydrateRoot(rootElement, tree);
  } else {
    // `/` is intentionally prerendered for crawlers, while app.html is an
    // empty shell for every authenticated or unknown SPA route. Clear any
    // static shell before createRoot so protected routes never flash landing
    // content and React does not warn about replacing existing children.
    if (rootElement.hasChildNodes()) rootElement.replaceChildren();
    createRoot(rootElement).render(tree);
  }
}

void mount();
